// 文件路径: frontend/shared/utils/holidays.ts
// 通用节假日服务（中国大陆） - 优先读取后端 constants 接口，保留第三方兜底

import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { constantsApi } from '../api/constants'

export type HolidayMarker = { date: string; tag: 'holiday' | 'workday'; name?: string }

dayjs.extend(isoWeek)
dayjs.extend(isSameOrBefore)

const KEY_PREFIX = 'ms_holidays_CN_'

function cacheKey(year: number) {
  return `${KEY_PREFIX}${year}`
}

function pruneCache(baseYear: number) {
  const keep = new Set<number>([baseYear - 1, baseYear, baseYear + 1])
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(KEY_PREFIX)) {
        const y = Number(key.replace(KEY_PREFIX, ''))
        if (!keep.has(y)) localStorage.removeItem(key)
      }
    })
  } catch {}
}

async function loadFromCache(year: number): Promise<{ holidays: Set<string>; workdays: Set<string> } | null> {
  try {
    const cached = localStorage.getItem(cacheKey(year))
    if (!cached) return null
    const parsed = JSON.parse(cached)
    return {
      holidays: new Set(parsed?.holidays || []),
      workdays: new Set(parsed?.workdays || []),
    }
  } catch {
    return null
  }
}

function saveToCache(year: number, holidays: Iterable<string>, workdays: Iterable<string>) {
  try {
    const payload = {
      holidays: Array.from(new Set(holidays)),
      workdays: Array.from(new Set(workdays)),
    }
    localStorage.setItem(cacheKey(year), JSON.stringify(payload))
    pruneCache(year)
  } catch {}
}

async function loadFromConstants(year: number): Promise<{ holidays: Set<string>; workdays: Set<string> } | null> {
  try {
    const items = await constantsApi.listLegalHolidaysByYear(year)
    if (!Array.isArray(items) || items.length === 0) return null
    const holidays = new Set<string>()
    const workdays = new Set<string>()
    items.forEach((item) => {
      if (!item?.date) return
      const date = dayjs(item.date).format('YYYY-MM-DD')
      if (item.is_working_day === true) {
        workdays.add(date)
      } else if (item.is_legal_holiday === true || item.is_working_day === false) {
        holidays.add(date)
      }
    })
    if (holidays.size === 0 && workdays.size === 0) return null
    saveToCache(year, holidays, workdays)
    return { holidays, workdays }
  } catch (error) {
    console.warn('[Holiday] constantsApi 列表获取失败，尝试回退', error)
    return null
  }
}

function formatRange(start: string, end: string) {
  const result: string[] = []
  let cursor = dayjs(start)
  const limit = dayjs(end)
  while (cursor.isSameOrBefore(limit)) {
    result.push(cursor.format('YYYY-MM-DD'))
    cursor = cursor.add(1, 'day')
  }
  return result
}

function officialCN2025Correction() {
  const holidays: string[] = []
  holidays.push('2025-01-01')
  holidays.push(...formatRange('2025-01-28', '2025-02-04'))
  holidays.push(...formatRange('2025-04-04', '2025-04-06'))
  holidays.push(...formatRange('2025-05-01', '2025-05-05'))
  holidays.push(...formatRange('2025-05-31', '2025-06-02'))
  holidays.push(...formatRange('2025-10-01', '2025-10-08'))
  const workdays = ['2025-01-26', '2025-02-08', '2025-04-27', '2025-09-28', '2025-10-11']
  return { holidays, workdays }
}

function applyOfficialCorrection(year: number, markers: HolidayMarker[]): HolidayMarker[] {
  if (year !== 2025) return markers
  const official = officialCN2025Correction()
  const map = new Map<string, HolidayMarker>()
  markers.forEach((marker) => {
    if (marker?.date) map.set(marker.date, marker)
  })
  official.holidays.forEach((date) => {
    map.set(date, { date, tag: 'holiday', name: '休' })
  })
  official.workdays.forEach((date) => {
    map.set(date, { date, tag: 'workday', name: '班' })
  })
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date))
}

async function loadFromBuiltinFallback(year: number): Promise<{ holidays: Set<string>; workdays: Set<string> }> {
  // 开发阶段兜底：使用内置的官方数据
  // 生产环境应始终依赖后端 constants/holidays/{year}/ 接口
  console.warn(`[Holiday] ${year} 年节假日数据从后端接口获取失败，使用内置兜底数据`)
  
  if (year === 2025) {
    const correction = officialCN2025Correction()
    const holidays = new Set(correction.holidays)
    const workdays = new Set(correction.workdays)
    saveToCache(year, holidays, workdays)
    return { holidays, workdays }
  }
  
  // 其他年份：返回空数据，提示配置后端接口
  console.warn(`[Holiday] ${year} 年无内置数据，请确保后端 constants/holidays/${year}/ 接口已配置`)
  return { holidays: new Set<string>(), workdays: new Set<string>() }
}

async function loadOfficialHolidayData(year: number): Promise<{ holidays: Set<string>; workdays: Set<string> }> {
  // 1. 优先使用本地缓存
  const cached = await loadFromCache(year)
  if (cached) return cached

  // 2. 从后端统一常量接口获取（唯一数据源）
  const fromConstants = await loadFromConstants(year)
  if (fromConstants) return fromConstants

  // 3. 兜底：使用内置官方数据（仅 2025 年）
  return loadFromBuiltinFallback(year)
}

export async function getCnHolidayMarkers(year: number, includeWorkdays = true): Promise<HolidayMarker[]> {
  const { holidays, workdays } = await loadOfficialHolidayData(year)

  const markers: HolidayMarker[] = []
  holidays.forEach((date) => {
    markers.push({ date, tag: 'holiday', name: '休' })
  })

  if (includeWorkdays) {
    workdays.forEach((date) => {
      markers.push({ date, tag: 'workday', name: '班' })
    })
  }

  return applyOfficialCorrection(year, markers)
}

export function summarizeMarkers(markers: HolidayMarker[]) {
  const holidays = markers.filter((marker) => marker.tag === 'holiday').length
  const workdays = markers.filter((marker) => marker.tag === 'workday').length
  return { holidays, workdays }
}
